import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { workScheduleApi, F90G } from '../../../services/workScheduleApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useWorkScheduleForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F90G[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [code, setCode] = useState('');
  const [effectiveDate, setEffectiveDate] = useState<string | null>(null);
  const [weekdayStart, setWeekdayStart] = useState('');
  const [weekdayEnd, setWeekdayEnd] = useState('');
  const [saturdayStart, setSaturdayStart] = useState('');
  const [saturdayEnd, setSaturdayEnd] = useState('');
  const [lunchStart, setLunchStart] = useState('');
  const [lunchEnd, setLunchEnd] = useState('');
  const [flexMinutes, setFlexMinutes] = useState(0);
  const [dailyHours, setDailyHours] = useState(0);
  const [remark, setRemark] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await workScheduleApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  const syncFields = useCallback((rec: F90G) => {
    setCode(rec.f90g_01);
    setEffectiveDate(rec.f90g_11);
    setWeekdayStart(rec.f90g_12 || '');
    setWeekdayEnd(rec.f90g_13 || '');
    setSaturdayStart(rec.f90g_14 || '');
    setSaturdayEnd(rec.f90g_15 || '');
    setLunchStart(rec.f90g_18 || '');
    setLunchEnd(rec.f90g_19 || '');
    setFlexMinutes(rec.f90g_17 || 0);
    setDailyHours(rec.f90g_20 || 0);
    setRemark(rec.f90g_16 || '');
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') {
      syncFields(current);
    }
  }, [current, mode, syncFields]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setCode('');
    setEffectiveDate(null);
    setWeekdayStart('');
    setWeekdayEnd('');
    setSaturdayStart('');
    setSaturdayEnd('');
    setLunchStart('');
    setLunchEnd('');
    setFlexMinutes(0);
    setDailyHours(0);
    setRemark('');
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    syncFields(current);
  };

  // Cancel
  const cancel = () => {
    setMode('browse');
    if (current) syncFields(current);
  };

  // Build payload from form fields
  const buildPayload = (): Partial<F90G> => ({
    f90g_01: code,
    f90g_11: effectiveDate,
    f90g_12: weekdayStart,
    f90g_13: weekdayEnd,
    f90g_14: saturdayStart,
    f90g_15: saturdayEnd,
    f90g_16: remark,
    f90g_17: flexMinutes,
    f90g_18: lunchStart,
    f90g_19: lunchEnd,
    f90g_20: dailyHours,
  });

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await workScheduleApi.create(buildPayload());
        message.success('新增成功');
      } else {
        await workScheduleApi.update(current!.f90g_01, buildPayload());
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetCode = mode === 'add' ? code : current!.f90g_01;
      const idx = data.findIndex(r => r.f90g_01 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await workScheduleApi.delete(current.f90g_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (searchCode: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f90g_01 >= searchCode);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode,
    effectiveDate, setEffectiveDate,
    weekdayStart, setWeekdayStart,
    weekdayEnd, setWeekdayEnd,
    saturdayStart, setSaturdayStart,
    saturdayEnd, setSaturdayEnd,
    lunchStart, setLunchStart,
    lunchEnd, setLunchEnd,
    flexMinutes, setFlexMinutes,
    dailyHours, setDailyHours,
    remark, setRemark,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  };
}
